import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle,
  ArrowRight,
  Download,
  Mail,
  Calendar,
  Phone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paymentMethod = searchParams.get("method") || "stripe";

  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const endpoint =
        paymentMethod === "paypal"
          ? `${API}/payments/paypal/status/${sessionId}`
          : `${API}/payments/stripe/status/${sessionId}`;

      const response = await axios.get(endpoint);

      if (response.data.payment_status === "paid" || response.data.status === "paid") {
        setStatus("success");
        setPaymentInfo(response.data);
        return;
      }

      // Continue polling
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-16 md:py-24" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="bg-green-500 py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto"
            >
              <CheckCircle className="w-14 h-14 text-green-500" />
            </motion.div>
          </div>
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {status === "checking" && (
                <>
                  <h1
                    className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Verifying Payment...
                  </h1>
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                </>
              )}

              {status === "success" && (
                <>
                  <h1
                    className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                    data-testid="success-title"
                  >
                    Payment Successful!
                  </h1>
                  <p className="text-slate-600 mb-8">
                    Thank you for your payment. Your booking is now confirmed and our
                    team will be in touch shortly.
                  </p>

                  {/* Payment Details */}
                  <div className="bg-slate-50 rounded-lg p-6 mb-8 text-left">
                    <h3 className="font-semibold text-slate-900 mb-4">Payment Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Payment ID:</span>
                        <span className="font-mono text-slate-900">{sessionId?.slice(0, 20)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Amount Paid:</span>
                        <span className="font-semibold text-green-600">
                          ${paymentInfo?.amount?.toFixed(2) || "—"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Payment Method:</span>
                        <span className="text-slate-900 capitalize">{paymentMethod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Status:</span>
                        <span className="text-green-600 font-medium">Confirmed</span>
                      </div>
                    </div>
                  </div>

                  {/* What's Next */}
                  <div className="bg-orange-50 rounded-lg p-6 mb-8 text-left">
                    <h3 className="font-semibold text-slate-900 mb-4">What's Next?</h3>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">
                          You'll receive a confirmation email with booking details
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">
                          Our team will call to confirm your appointment
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">
                          A technician will arrive at your scheduled time
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12 px-6"
                      data-testid="back-home-btn"
                    >
                      <Link to="/">
                        Back to Home
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-2 border-slate-900 text-slate-900 font-semibold h-12 px-6"
                      data-testid="book-another-btn"
                    >
                      <Link to="/booking">Book Another Service</Link>
                    </Button>
                  </div>
                </>
              )}

              {status === "timeout" && (
                <>
                  <h1
                    className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Payment Processing
                  </h1>
                  <p className="text-slate-600 mb-8">
                    We're still processing your payment. Please check your email for
                    confirmation or contact us if you have any questions.
                  </p>
                  <Button
                    asChild
                    className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12 px-8"
                  >
                    <Link to="/contact">Contact Support</Link>
                  </Button>
                </>
              )}
            </motion.div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Need help? Call us at <a href="tel:+17587230202" className="text-orange-500 hover:underline">+17587230202</a></p>
        </div>
      </div>
    </div>
  );
}
